import type { Practice, WebsiteState } from "@brandex/api-client";
import type { MessageKey } from "@brandex/i18n";
import { accentHex, Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from "@brandex/ui";
import { ExternalLink, Globe, ShieldCheck } from "lucide-react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { useCurrentOrg } from "../client-scope";
import { usePractice, websiteUrl } from "../queries";

const TONE: Record<WebsiteState, "success" | "warning" | "neutral"> = {
  live: "success",
  awaiting_activation: "warning",
  awaiting_verification: "warning",
  offline: "warning",
  unpublished: "warning",
  not_included: "neutral",
  needs_onboarding: "neutral",
};

/**
 * Where the doctor's BMDC registration check stands (ADR 0016 §4). The
 * website goes live only once BrandEx has verified it.
 */
export function VerificationNotice({ practice, compact = false }: { practice: Practice; compact?: boolean }) {
  const t = useT();
  const { nav } = useBrandex();
  const v = practice.profile.verification;
  const fix = <nav.Link href="/app/profile#details" className="font-medium underline">{t("verification.fix")}</nav.Link>;
  if (v.status === "verified") {
    return compact ? null : (
      <Alert tone="success"><span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" aria-hidden />{t("verification.verified", { number: practice.profile.registration_no ?? "" })}</span></Alert>
    );
  }
  if (v.status === "pending") return <Alert tone="info">{t("verification.pending", { number: practice.profile.registration_no ?? "" })}</Alert>;
  if (v.status === "rejected") {
    return (
      <Alert tone="error">
        {t("verification.rejected", { reason: v.reason ?? "" })} {fix}
      </Alert>
    );
  }
  return <Alert tone="info">{t("verification.notSubmitted")} {fix}</Alert>;
}

/** "View my site": opens the live site in a new tab or the OS browser. */
export function ViewSiteButton({ practice, className }: { practice: Practice; className?: string }) {
  const t = useT();
  const { platform, siteUrl } = useBrandex();
  if (practice.website.state !== "live") return null;
  return (
    <Button variant="outline" size="sm" className={className} onClick={() => void platform.openExternal(websiteUrl(practice.website, siteUrl))}>
      <ExternalLink className="h-4 w-4" aria-hidden />
      {t("website.view")}
    </Button>
  );
}

/** Doctor "My website" (F-07): address, status and what patients see. */
export function WebsiteView() {
  const t = useT();
  const { nav, siteUrl } = useBrandex();
  const orgs = useCurrentOrg();
  const orgId = orgs.orgId;
  const practice = usePractice(orgId);
  if (orgs.isPending || (orgId !== undefined && practice.isPending)) return <Spinner label={t("common.loading")} />;
  if (orgs.isError || practice.isError) return <Alert tone="error">{errorMessage(orgs.error ?? practice.error, t)}</Alert>;
  if (!practice.data) {
    return (
      <div className="mx-auto grid max-w-3xl gap-4">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("website.title")}</h1>
        <Alert tone="info">{t("website.state.needs_onboarding")}</Alert>
        <div><Button onClick={() => nav.navigate("/onboarding")}>{t("onboarding.continue")}</Button></div>
      </div>
    );
  }
  const p = practice.data;
  const { website, profile, chambers } = p;
  const url = websiteUrl(website, siteUrl);
  return (
    <div className="mx-auto grid max-w-3xl gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("website.title")}</h1>
        <ViewSiteButton practice={p} />
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" aria-hidden />
              {t("website.address")}
            </CardTitle>
            <Badge tone={TONE[website.state]}>{t(`website.state.${website.state}` as MessageKey)}</Badge>
          </div>
          <CardDescription className="break-all font-mono text-sm">{url}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm">
          {website.state === "awaiting_activation" ? <Alert tone="info">{t("website.awaitingHint", { url })}</Alert> : null}
          {website.state === "awaiting_verification" || p.profile.verification.status !== "verified" ? <VerificationNotice practice={p} /> : null}
          {website.state === "offline" ? <Alert tone="error">{t("website.offlineHint")}</Alert> : null}
          {website.state === "unpublished" ? (
            <Alert tone="info">
              {t("profile.unpublishedHint")}{" "}
              <nav.Link href="/app/profile" className="font-medium underline">{t("profile.title")}</nav.Link>
            </Alert>
          ) : null}
          {website.state === "not_included" ? (
            <Alert tone="info">
              {t("website.upgrade")}{" "}
              <nav.Link href="/app/package" className="font-medium underline">{t("website.openPackage")}</nav.Link>
            </Alert>
          ) : null}
          <div>
            <p className="font-medium">{t("website.customDomain")}</p>
            <p className="text-muted-foreground">{website.domain ? `${website.domain} — ${t("website.domainConnected")}` : t("website.noDomain")}</p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t("website.contents")}</CardTitle>
          <CardDescription>{t("website.contentsHint")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex items-center gap-3 rounded-[var(--radius-lg)] p-4 text-white" style={{ background: accentHex(profile.brand.accent) }}>
            <span className="grid h-12 w-12 place-items-center rounded-full bg-white/20 font-semibold">{profile.initials}</span>
            <span className="grid">
              <span className="font-display text-lg font-semibold">{profile.full_name}</span>
              <span className="text-sm opacity-90">{t(`specialty.${profile.specialty}` as MessageKey)} · {profile.designation}</span>
            </span>
          </div>
          <p className="text-sm leading-relaxed">{profile.bio}</p>
          <ul className="grid gap-2 text-sm">
            {chambers.map((c) => (
              <li key={c.id} className="rounded-md border border-border px-3 py-2">
                <span className="font-medium">{c.name}</span> · <span className="text-muted-foreground">{c.address}</span>
                <span className="block text-muted-foreground">
                  {c.days.map((d) => t(`day.${d}` as MessageKey)).join(", ")} · {c.opens}–{c.closes} · {t("onboarding.minutesValue", { n: c.slot_minutes })}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{profile.languages.map((l) => t(`language.${l}` as MessageKey)).join(" · ")}</p>
        </CardContent>
      </Card>
    </div>
  );
}
